import React, { useState } from "react";
import CustomButton from "../Buttons/CustomButton";
import CustomImage from "../../Utility/CustomImage";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageModal from "./ImageModal";
import { getOrdinalSuffix } from "../../Utility/Utils";
import { RenderDocumentView } from "../../Utility/Utils";

const TenantDetailsModal = ({
  isOpen,
  onClose,
  unitDetails,
  selectedTenant,
  allAgreementTenants = [],
  jwt,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (imageLocation) => {
    setSelectedImage(imageLocation);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  if (!isOpen) return null;

  // Use selectedTenant if provided, otherwise fall back to unitDetails.tenant
  const currentTenant = selectedTenant || unitDetails?.tenant;

  if (!currentTenant) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-1/2">
          <p>No tenant information available</p>
          <CustomButton
            tailwindClasses="bg-blue-500 text-white mt-4"
            label="Close"
            onClick={onClose}
          />
        </div>
      </div>
    );
  }

  // Get other tenants (exclude the current selected tenant)
  const otherTenants = allAgreementTenants.filter(
    (tenant) => tenant.entity_id !== currentTenant.entity_id
  );

  // Helper function to get tenant initials
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : "";
    const last = lastName ? lastName.charAt(0).toUpperCase() : "";
    return first + last || "T";
  };

  // Helper function to render tenant avatar
  const renderTenantAvatar = (tenant, size = "h-12 w-12") => {
    const initials = getInitials(tenant.first_name, tenant.last_name);

    if (tenant.media?.profile_picture?.location) {
      return (
        <CustomImage
          location={tenant.media.profile_picture.location}
          jwt={jwt}
          initials={initials}
          className={`${size} rounded-full object-cover`}
        />
      );
    } else {
      return (
        <div
          className={`${size} flex items-center justify-center rounded-full bg-red-400 text-white font-semibold`}
        >
          {initials}
        </div>
      );
    }
  };

  // Check if current tenant is primary (first in the list)
  const isPrimaryTenant =
    allAgreementTenants.length > 0 &&
    allAgreementTenants[0]?.entity_id === currentTenant.entity_id;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-1/2 max-h-[90vh] overflow-y-auto relative">
        <h2 className="text-xl font-bold mb-4 text-center">
          Tenant Information
        </h2>

        {/* Main Tenant Info */}
        <div className="mb-4 flex gap-4 items-center">
          {renderTenantAvatar(currentTenant, "h-20 w-20")}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-lg font-semibold">
                {currentTenant.first_name} {currentTenant.last_name}
              </p>
              {isPrimaryTenant && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
                  Primary
                </span>
              )}
            </div>
            <p className="text-gray-600">{currentTenant.mobile}</p>
            <p className="text-sm text-gray-500">{currentTenant.email}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => window.open(`tel:${currentTenant.mobile}`)}
              className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
            >
              📞
            </button>
            <button
              onClick={() =>
                window.open(`https://wa.me/${currentTenant.mobile}`)
              }
              className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
            >
              💬
            </button>
          </div>
        </div>

        <div className="accordian-container">
          {/* Agreement Details */}
          <Accordion className="agreement-accordian">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="agreement-accordian"
              className="text-sm"
            >
              Agreement Details
            </AccordionSummary>
            <AccordionDetails className="flex flex-col justify-between gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold">
                    {unitDetails?.unit?.unit_name}
                  </p>
                  <p className="text-gray-600">
                    {unitDetails?.unit?.building_name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monthly Rent</p>
                  <p className="font-semibold">
                    ₹
                    {unitDetails?.unit?.localRent ||
                      unitDetails?.agreement?.rent?.[0]?.rent}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p>
                    {getOrdinalSuffix(unitDetails?.agreement?.due_date)} of
                    every month
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Security Deposit</p>
                  <p>₹{unitDetails?.agreement?.security_deposit}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Staying From</p>
                  <p>
                    {unitDetails?.agreement?.staying_from
                      ? new Date(
                          unitDetails.agreement.staying_from
                        ).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tenure</p>
                  <p>{unitDetails?.agreement?.tenure}</p>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Other Tenants */}
          {otherTenants.length > 0 && (
            <Accordion className="other-tenants-accordian">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="other-tenants-accordian"
                className="text-sm"
              >
                Other Tenants ({otherTenants.length})
              </AccordionSummary>
              <AccordionDetails>
                <div className="space-y-3">
                  {otherTenants.map((tenant, index) => {
                    const isFirst =
                      allAgreementTenants[0]?.entity_id === tenant.entity_id;
                    return (
                      <div
                        key={tenant.entity_id || index}
                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                      >
                        {renderTenantAvatar(tenant)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">
                              {tenant.first_name} {tenant.last_name}
                            </p>
                            {isFirst && (
                              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                Primary
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm">
                            {tenant.mobile}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => window.open(`tel:${tenant.mobile}`)}
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            📞
                          </button>
                          <button
                            onClick={() =>
                              window.open(`https://wa.me/${tenant.mobile}`)
                            }
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            💬
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Documents */}
          <Accordion className="documents-accordian">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel3-content"
              id="documents-accordian"
              className="text-sm"
            >
              Documents
            </AccordionSummary>
            <AccordionDetails className="flex flex-col justify-between gap-2">
              {currentTenant.media?.aadhar_front && (
                <button
                  onClick={() =>
                    handleImageClick(currentTenant.media.aadhar_front.location)
                  }
                  className="text-left text-blue-500 hover:text-blue-700 p-2 border rounded hover:bg-blue-50"
                >
                  📄 View Aadhar Front
                </button>
              )}
              {currentTenant.media?.aadhar_back && (
                <button
                  onClick={() =>
                    handleImageClick(currentTenant.media.aadhar_back.location)
                  }
                  className="text-left text-blue-500 hover:text-blue-700 p-2 border rounded hover:bg-blue-50"
                >
                  📄 View Aadhar Back
                </button>
              )}
              {currentTenant.media?.pan && (
                <button
                  onClick={() =>
                    handleImageClick(currentTenant.media.pan.location)
                  }
                  className="text-left text-blue-500 hover:text-blue-700 p-2 border rounded hover:bg-blue-50"
                >
                  📄 View PAN Card
                </button>
              )}
              {currentTenant.media?.rent_agreement && (
                <button
                  onClick={() =>
                    handleImageClick(
                      currentTenant.media.rent_agreement.location
                    )
                  }
                  className="text-left text-blue-500 hover:text-blue-700 p-2 border rounded hover:bg-blue-50"
                >
                  📄 View Rent Agreement
                </button>
              )}
              {currentTenant.media?.profile_picture && (
                <button
                  onClick={() =>
                    handleImageClick(
                      currentTenant.media.profile_picture.location
                    )
                  }
                  className="text-left text-blue-500 hover:text-blue-700 p-2 border rounded hover:bg-blue-50"
                >
                  📷 View Profile Picture
                </button>
              )}

              {/* Show message if no documents */}
              {!currentTenant.media ||
                (Object.keys(currentTenant.media).length === 0 && (
                  <p className="text-gray-500 italic p-2">
                    No documents uploaded
                  </p>
                ))}
            </AccordionDetails>
          </Accordion>
        </div>

        <div className="flex justify-end mt-4">
          <CustomButton
            tailwindClasses="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
            label="Close"
            onClick={onClose}
          />
        </div>

        {/* Image Modal */}
        <ImageModal isOpen={!!selectedImage} onClose={handleCloseModal}>
          <CustomImage
            location={selectedImage}
            jwt={jwt}
            className="max-w-full max-h-full object-contain"
          />
        </ImageModal>
      </div>
    </div>
  );
};

export default TenantDetailsModal;
